import {
  ChartColumnBig,
  FolderOpen,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import { Fragment, type ReactNode, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { m } from "framer-motion";
import {
  BreakdownBarList,
  BreakdownDonutChart,
  Button,
  Combobox,
  type ComboboxOption,
  DateRangePicker,
  type DateRangeComparisonMode,
  type DateRangePreset,
  type DateRangeSelection,
  MultiSelect,
  resolveComparisonRange,
  resolveDateRange,
  resolvePresetRange,
  Separator,
  Skeleton,
  StatCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TrendBarChart,
} from "@rumbledirect/ui-v2";
import { Panel } from "@/dashboard/Panel";
import { StatusDistributionBar } from "@/dashboard/StatusDistributionBar";
import { deriveStatusSlices } from "@/dashboard/statusDistribution";
import { DOSSIER_KPI_TILE_META } from "@/dossiers/dossierKpiTiles";
import { DOSSIER_TYPE_OPTIONS } from "@/dossiers/dossierTypeLabels";
import { REPAIR_TYPE_OPTIONS } from "@/dossiers/repairTypeLabels";
import {
  CLAIMABLE_OPTIONS,
  EXTERNAL_INSURED_OPTIONS,
} from "@/dossiers/dossierReportFilters";
import { useDossierStats } from "@/dossiers/useDossierStats";
import { useDossierStatusLookup } from "@/dossiers/dossierStatusLookup";
import { useDossierStatusStats } from "@/dossiers/useDossierStatusStats";
import {
  END_OF_CONTRACT_LABEL,
  END_OF_CONTRACT_OPTIONS,
  type EndOfContract,
  toEndOfContract,
} from "@/dossiers/endOfContractFilter";
import { useGetAdminVehicleBrands } from "@/lib/api/generated/vehicle/vehicle";
import { parseISODate, toISODate } from "@rumbledirect/ui-v2/day";
import { cn } from "@rumbledirect/ui-v2/cn";
import { formatDate, formatNumber } from "@rumbledirect/ui-v2/format";
import {
  toNonEmptyString,
  toStringList,
  toThreeStateBoolean,
} from "@/lib/searchParams";
import {
  booleanToOptionValue,
  optionValueToBoolean,
} from "@/lib/threeStateBoolean";
import { toVehicleBrandOptions } from "@/vehicles/vehicleBrandOptions";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import {
  ANALYTICS_REQUEST_OPTIONS,
  type AnalyticsFilterSelection,
  type AnalyticsParams,
  toAnalyticsFilterParams,
  toAnalyticsRange,
  toBreakdownEntries,
  toPercentageDelta,
} from "@/reporting/analytics";
import {
  ANALYTICS_KPIS,
  type AnalyticsKpiTile,
  type BaseChartEntry,
  type BreakdownChartEntry,
  type PhotosPerDossierChartEntry,
  REPORTING_CHARTS,
  type ReportingChartEntry,
  type TrendChartEntry,
  toChartSegments,
  toPhotoCountSegments,
  toTrendPoints,
  toTrendRange,
} from "@/reporting/reportingCharts";
import {
  DEFAULT_REPORTING_LAYOUT,
  type ReportingFilter,
  type ReportingKpi,
  type ReportingSection,
  type TopLevelReportingSection,
} from "@/reporting/reportingLayout";
import {
  NotRecordedProvider,
  useReportNotRecorded,
} from "@/reporting/notRecorded";
import { useReportingLayout } from "@/reporting/useReportingLayout";
import {
  staggerContainer,
  staggerItem,
  useMotionInitial,
} from "@rumbledirect/ui-v2/motion";

const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  "last30Days",
  "thisMonth",
  "previousMonth",
  "thisYear",
  "custom",
];

const DATE_RANGE_COMPARISON_MODES: readonly DateRangeComparisonMode[] = [
  "previousPeriod",
  "previousYear",
  "custom",
];

/**
 * The longest custom (or custom comparison) range the picker may commit.
 * Mirrors the backend's `MediaIntentPerDayInput::MAX_PERIOD_DAYS`: it is the
 * only `data-analytics` input that caps the period, and `mediaIntentPerDay`
 * is a section the default configuration actually places on this page — a
 * longer custom range would otherwise commit, then fail that one chart with
 * a validation error a retry can never fix. Applied globally rather than
 * only when that section is configured, so the cap does not silently change
 * behaviour based on what an affiliate happens to have set up.
 */
const MAX_REPORT_PERIOD_DAYS = 366;

/**
 * The same nav permission `shell/navItems.tsx` gates the "Rapportage" link
 * on. Hiding the link is not access control: a user who already knows the
 * URL (or a bookmark from before their permissions changed) can still
 * navigate straight to it, so the page itself must check too.
 */
const ANALYTICS_READ_PERMISSION = "admin_analytics_nav";

/** Shown instead of the page to a user who lacks {@link ANALYTICS_READ_PERMISSION}. */
function ReportingForbidden(): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-2xl border border-border/70 bg-surface py-16 text-center text-muted-foreground shadow-xs"
    >
      <p>Je hebt geen toegang tot deze pagina.</p>
    </div>
  );
}

interface ReportingSearch {
  /** The active tab key; absent means "the first configured tab". */
  readonly tab?: string;
  readonly preset?: DateRangePreset;
  readonly start?: string;
  readonly end?: string;
  readonly compare?: boolean;
  readonly comparisonMode?: DateRangeComparisonMode;
  /** Custom comparison range, only meaningful with `comparisonMode=custom`. */
  readonly cstart?: string;
  readonly cend?: string;
  /**
   * Three-state insurance filter (RK-1230): `undefined` is "no choice" (no
   * filter sent), `false` is internally insured — a deliberate choice the
   * backend acts on, so it must never collapse into "unset". Same semantics
   * as the dossier overview's (`dossiers/dossiersSearch.ts`).
   */
  readonly externalInsured?: boolean;
  /** Three-state claimability (verhaalbaarheid) filter — same semantics as `externalInsured`. */
  readonly claimable?: boolean;
  /** Scopes the whole report to dossiers whose vehicle brand is any of these values. */
  readonly brand: readonly string[];
  /** Three-state selection; rule IDs are resolved from the served layout. */
  readonly endOfContract: EndOfContract | undefined;
  /** Scopes the whole report to one dossier type (a `DossierType` discriminator value). */
  readonly dossierType: string | undefined;
  /** Scopes the whole report to one repair type (a `RepairType` enum value). */
  readonly repairType: string | undefined;
}

/**
 * The persisted day pair as parsed, ordered dates — or `null` when either
 * day is missing, does not parse, lies after the other, reaches past
 * `maxISO`, or spans more than {@link MAX_REPORT_PERIOD_DAYS}. A report
 * period cannot include days that have not happened yet: the underlying
 * `data-analytics` endpoints have no data for them, and a comparison against
 * a shorter, still-elapsing period would silently compare unequal window
 * lengths.
 */
function toValidRange(
  start: string | undefined,
  end: string | undefined,
  maxISO: string,
): { start: string; end: string } | null {
  if (start === undefined || end === undefined) {
    return null;
  }
  const startDate = parseISODate(start);
  const endDate = parseISODate(end);
  if (
    startDate === null ||
    endDate === null ||
    endDate < startDate ||
    // ISO days are zero-padded, so lexicographic comparison is chronological
    // (mirroring `toAnalyticsRange`'s own comparison against "today").
    start > maxISO ||
    end > maxISO
  ) {
    return null;
  }
  // Both boundaries count as a day, so the span is the difference plus one —
  // the same inclusive counting `MediaIntentPerDayInput` applies server-side.
  const spanDays =
    Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1;
  if (spanDays > MAX_REPORT_PERIOD_DAYS) {
    return null;
  }
  return { start, end };
}

// Content-only: the authenticated AppShell owns the single <main> landmark
// and page layout container.
export const Route = createFileRoute("/_authenticated/reporting")({
  // The active tab and the report period live in the URL so a deep
  // link/reload lands back on the same view, mirroring the driver detail
  // route. `tab` stays a plain string: the configured tab keys are not known
  // until the layout resolves, so the component (not the search validator)
  // decides whether a key is selectable.
  //
  // validateSearch TYPES the period params only; what a valid period IS
  // lives in `toPeriodSelection`, the single normaliser. That split is not
  // optional: the app does not set the router's `search.strict`, so raw URL
  // params survive validateSearch dropping them and reach the component
  // anyway — a validator that also decided validity would be trusting values
  // it meant to drop.
  validateSearch: (search: Record<string, unknown>): ReportingSearch => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
    preset:
      typeof search.preset === "string"
        ? (search.preset as DateRangePreset)
        : undefined,
    start: typeof search.start === "string" ? search.start : undefined,
    end: typeof search.end === "string" ? search.end : undefined,
    // `undefined` when off, not `false`: TanStack drops undefined keys from
    // the URL, so the default never materialises as `?compare=false`.
    compare: search.compare === true ? true : undefined,
    comparisonMode:
      typeof search.comparisonMode === "string"
        ? (search.comparisonMode as DateRangeComparisonMode)
        : undefined,
    cstart: typeof search.cstart === "string" ? search.cstart : undefined,
    cend: typeof search.cend === "string" ? search.cend : undefined,
    // Three-state, unlike `compare` above: absent and malformed both mean
    // "no filter", and `false` is a real choice.
    externalInsured: toThreeStateBoolean(search.externalInsured),
    claimable: toThreeStateBoolean(search.claimable),
    brand: toStringList(search.brand),
    endOfContract: toEndOfContract(search.endOfContract),
    dossierType: toNonEmptyString(search.dossierType),
    repairType: toNonEmptyString(search.repairType),
  }),
  component: ReportingPage,
});

/** Every possible KPI tile the "dossierKpis" section renders, unfiltered. */
const REPORTING_KPIS = ["total", "unread", "mine", "actionRequired"] as const;

/**
 * The default period: "last 30 days", comparison off. Resolved per call so
 * the anchor ("today") is never frozen at module scope.
 */
function defaultPeriodSelection(): DateRangeSelection {
  return {
    preset: "last30Days",
    range: resolvePresetRange("last30Days"),
    compare: false,
    comparisonMode: "previousPeriod",
  };
}

/**
 * Normalises the raw search params into the picker's `DateRangeSelection` —
 * the single source of truth about what a valid period is. EVERY field is
 * checked here (the typed `ReportingSearch` cannot be trusted: without the
 * router's `search.strict`, raw URL params reach the component alongside
 * what validateSearch returned), and ANY invalid field or combination
 * collapses the whole period to the default atomically — a partially valid
 * period is not a period:
 *
 * - an unknown `preset`, or `preset=custom` without a valid, ordered
 *   `start`/`end` that does not reach into the future or span more than
 *   {@link MAX_REPORT_PERIOD_DAYS};
 * - an unknown `comparisonMode`;
 * - `comparisonMode=custom` without a valid, ordered `cstart`/`cend` that
 *   does not reach into the future or span more than
 *   {@link MAX_REPORT_PERIOD_DAYS};
 * - `cstart`/`cend` present while `compare` is off or the mode is not
 *   `custom` (a comparison range nobody asked for).
 *
 * The concrete ranges resolve through the picker's own
 * `resolveDateRange`/`resolveComparisonRange`, so the preset maths lives in
 * exactly one place. For a non-custom preset the resolved start/end are
 * deliberately NOT persisted in the URL: a shared or reloaded "last 30 days"
 * link must keep meaning "the last 30 days", so the range is re-resolved per
 * committed URL selection rather than persisted. Only a `custom` preset
 * round-trips its range through `start`/`end` (and a custom comparison range
 * through `cstart`/`cend`).
 */
function toPeriodSelection(search: ReportingSearch): DateRangeSelection {
  const preset = DATE_RANGE_PRESETS.includes(search.preset as DateRangePreset)
    ? (search.preset as DateRangePreset)
    : undefined;
  const compare = search.compare === true;
  const comparisonMode = DATE_RANGE_COMPARISON_MODES.includes(
    search.comparisonMode as DateRangeComparisonMode,
  )
    ? (search.comparisonMode as DateRangeComparisonMode)
    : undefined;

  const todayISO = toISODate(new Date());
  const customRange =
    preset === "custom"
      ? toValidRange(search.start, search.end, todayISO)
      : null;
  const customComparison =
    compare && comparisonMode === "custom"
      ? toValidRange(search.cstart, search.cend, todayISO)
      : null;

  if (
    (search.preset !== undefined && preset === undefined) ||
    (preset === "custom" && customRange === null) ||
    (search.comparisonMode !== undefined && comparisonMode === undefined) ||
    (compare && comparisonMode === "custom" && customComparison === null) ||
    ((search.cstart !== undefined || search.cend !== undefined) &&
      !(compare && comparisonMode === "custom"))
  ) {
    return defaultPeriodSelection();
  }

  const range = resolveDateRange({
    preset: preset ?? "last30Days",
    customRange: customRange ?? undefined,
  });
  const comparisonRange = compare
    ? resolveComparisonRange(range, {
        comparisonMode: comparisonMode ?? "previousPeriod",
        customComparisonRange: customComparison ?? undefined,
      })
    : undefined;

  return {
    preset: preset ?? "last30Days",
    range,
    compare,
    comparisonMode: comparisonMode ?? "previousPeriod",
    comparisonRange,
    customRange: customRange ?? undefined,
    customComparisonRange: customComparison ?? undefined,
  };
}

/**
 * Turns a committed picker selection back into search params. Only the
 * preset, comparison switch and mode are persisted for non-custom presets:
 * the resolved start/end are derived, so storing them would freeze a
 * relative period ("last 30 days") into absolute dates. For `custom` the
 * resolved range is the value, so it is persisted — likewise `cstart`/`cend`
 * for a custom comparison range.
 */
function dateRangeSelectionToSearch(
  selection: DateRangeSelection,
): Pick<
  ReportingSearch,
  "preset" | "start" | "end" | "compare" | "comparisonMode" | "cstart" | "cend"
> {
  const base = {
    preset: selection.preset,
    compare: selection.compare || undefined,
    comparisonMode: selection.compare ? selection.comparisonMode : undefined,
    cstart:
      selection.compare && selection.comparisonMode === "custom"
        ? selection.comparisonRange?.start
        : undefined,
    cend:
      selection.compare && selection.comparisonMode === "custom"
        ? selection.comparisonRange?.end
        : undefined,
  };

  if (selection.preset !== "custom") {
    return { ...base, start: undefined, end: undefined };
  }

  return { ...base, start: selection.range.start, end: selection.range.end };
}

/**
 * The range every chart on this page shares, derived from the picker's
 * committed selection. `toAnalyticsRange` drops the `end` only when it would
 * land in the future, so every chart query keys off the same params.
 */
function selectionToChartRange(selection: DateRangeSelection): AnalyticsParams {
  const start = parseISODate(selection.range.start);
  const end = parseISODate(selection.range.end);
  if (start && end) {
    return toAnalyticsRange(start, end);
  }
  // Unreachable once validateSearch has run (a broken range already fell back
  // to the default selection), but a hand-rolled caller must not error.
  const today = new Date();
  return toAnalyticsRange(today, today);
}

/** The minimal query state the section cards branch on. */
interface SectionQuery {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => Promise<unknown>;
}

/**
 * The pending/failed chrome every section card shares: an accessible
 * skeleton while loading, a contained retryable error on failure, and the
 * loaded body otherwise — one failed card must never blank the page. The
 * skeleton shape differs per card (a chart's plot area vs a KPI figure), so
 * it is the caller's, not a config flag.
 */
function QueryStatePanel({
  entry,
  query,
  skeleton,
  errorMessage,
  children,
}: {
  readonly entry: BaseChartEntry;
  readonly query: SectionQuery;
  readonly skeleton: ReactNode;
  readonly errorMessage: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <Panel title={entry.title}>
      {query.isPending ? (
        <div role="status" aria-live="polite">
          <span className="sr-only">{entry.title} wordt geladen…</span>
          {skeleton}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 py-10 text-center"
        >
          <p className="text-muted-foreground">{errorMessage}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : (
        children
      )}
    </Panel>
  );
}

/**
 * The card chrome every chart shares: the shared query-state panel, and the
 * chart itself once the query resolves.
 */
function ChartCard({
  entry,
  query,
  labels,
}: {
  readonly entry: ReportingChartEntry;
  readonly query: SectionQuery & { readonly data: unknown };
  readonly labels?: Readonly<Record<string, string>>;
}): ReactNode {
  return (
    <QueryStatePanel
      entry={entry}
      query={query}
      skeleton={<Skeleton aria-hidden="true" className="h-40 w-full" />}
      errorMessage="Deze grafiek kon niet worden geladen."
    >
      <ChartView entry={entry} data={query.data} labels={labels} />
    </QueryStatePanel>
  );
}

/**
 * One chart card of a tab. Each configured section gets its own component
 * instance (keyed by section id) so it owns its own query, and the registry
 * lookup happens once per instance: a hook must never change identity under
 * a stable instance. The per-kind components below exist so the params each
 * hook is called with keep their generated type — the trend endpoint's
 * required `intent` would otherwise widen onto every entry.
 */
function ReportingChartCard({
  entry,
  params,
}: {
  readonly entry: ReportingChartEntry;
  /** The shared range plus the active report filters (RK-1230). */
  readonly params: AnalyticsParams;
}): ReactNode {
  if (entry.chart.kind === "trend") {
    return <TrendChartCard entry={entry as TrendChartEntry} params={params} />;
  }
  return (
    <BreakdownChartCard
      entry={entry as BreakdownChartEntry | PhotosPerDossierChartEntry}
      params={params}
    />
  );
}

// Exported for the remote-labels card tests
// (`src/reporting/remoteLabelsChartCard.test.tsx`): rendering the real card
// against MSW is what proves raw enum keys never flash before the backend
// labels arrive.
export { ReportingChartCard };

function TrendChartCard({
  entry,
  params,
}: {
  readonly entry: TrendChartEntry;
  readonly params: AnalyticsParams;
}): ReactNode {
  // entry.useQuery is now the same AnalyticsQueryHook<AnalyticsParams>
  // contract every other chart entry's useQuery is — no fixed params to
  // merge and no cast: the hook that needs one (media-intent-per-day) closes
  // over it itself (see reportingCharts.tsx's useDamagePhotosPerDayTrend).
  const query = entry.useQuery(params, { request: ANALYTICS_REQUEST_OPTIONS });
  return <ChartCard entry={entry} query={query} />;
}

function BreakdownChartCard({
  entry,
  params,
}: {
  readonly entry: BreakdownChartEntry | PhotosPerDossierChartEntry;
  readonly params: AnalyticsParams;
}): ReactNode {
  const query = entry.useQuery(params, { request: ANALYTICS_REQUEST_OPTIONS });
  // A separate component owns the remote-labels case so the label hook is
  // called unconditionally there — an optional call here would violate
  // useHookAtTopLevel (the entry is stable per instance, but the rule does
  // not know that).
  if ("useLabels" in entry && entry.useLabels !== undefined) {
    return (
      <RemoteLabelsBreakdownChartCard
        entry={entry}
        query={query}
        useLabels={entry.useLabels}
      />
    );
  }
  return (
    <ChartCard
      entry={entry}
      query={query}
      labels={"labels" in entry ? entry.labels : undefined}
    />
  );
}

/**
 * A breakdown chart whose labels come from a backend enum collection rather
 * than a static map. The label query is treated like the data query: the
 * card stays pending until both resolve, and a failed label query surfaces
 * the card's error state (retry refetches both) — raw enum keys must never
 * flash on screen.
 */
function RemoteLabelsBreakdownChartCard({
  entry,
  query,
  useLabels,
}: {
  readonly entry: BreakdownChartEntry;
  readonly query: SectionQuery & { readonly data: unknown };
  readonly useLabels: NonNullable<BreakdownChartEntry["useLabels"]>;
}): ReactNode {
  const labelsQuery = useLabels();
  const mergedQuery: SectionQuery & { readonly data: unknown } = {
    data: query.data,
    isPending: query.isPending || labelsQuery.isPending,
    isError: query.isError || labelsQuery.isError,
    refetch: async () => {
      await Promise.all([query.refetch(), labelsQuery.refetch()]);
    },
  };
  return (
    <ChartCard entry={entry} query={mergedQuery} labels={labelsQuery.data} />
  );
}

/**
 * One tile of the KPI row: a `StatCard` whose value composes the
 * tile's extracted figure with a delta against the comparison period when the
 * picker has comparison enabled. The comparison query is gated on
 * `comparisonRange` being defined, so it only fires when the user asked for
 * it — but once asked for, it is folded into the tile's state: the tile is
 * pending while either query is pending and errored when either fails (retry
 * refetches both), because a delta-less tile would otherwise be
 * indistinguishable from "no meaningful delta".
 *
 * The delta renders as an arrow plus a signed figure — never colour alone —
 * and is omitted when the previous value is unknown (or, for count tiles,
 * zero — dividing by it is a bug; `toPercentageDelta` guards that). Count
 * tiles compare with `toPercentageDelta`; percentage tiles compare in
 * percentage POINTS (the prototype is explicit: a percentage of a percentage
 * is noise), so a 0% comparison baseline still yields a delta.
 */
function AnalyticsKpiCard({
  tile,
  params,
  comparisonParams,
}: {
  readonly tile: AnalyticsKpiTile;
  /** The shared range plus the active report filters (RK-1230). */
  readonly params: AnalyticsParams;
  /** The comparison range carrying the same filters, when comparison is on. */
  readonly comparisonParams: AnalyticsParams | undefined;
}): ReactNode {
  const query = tile.useQuery(params, { request: ANALYTICS_REQUEST_OPTIONS });
  const comparisonQuery = tile.useQuery(comparisonParams ?? params, {
    query: { enabled: comparisonParams !== undefined },
    request: ANALYTICS_REQUEST_OPTIONS,
  });

  const isPending =
    query.isPending ||
    (comparisonParams !== undefined && comparisonQuery.isPending);
  const isError =
    query.isError ||
    (comparisonParams !== undefined && comparisonQuery.isError);

  if (isPending || isError) {
    return (
      <QueryStatePanel
        entry={{ title: tile.label }}
        query={{
          isPending,
          isError,
          refetch: async () => {
            await query.refetch();
            if (comparisonParams !== undefined) {
              await comparisonQuery.refetch();
            }
          },
        }}
        skeleton={<Skeleton aria-hidden="true" className="h-9 w-24" />}
        errorMessage="Dit kerncijfer kon niet worden geladen."
      >
        {null}
      </QueryStatePanel>
    );
  }

  const value = tile.toValue(query.data);
  const comparisonValue = comparisonParams
    ? tile.toValue(comparisonQuery.data)
    : undefined;
  const delta = comparisonParams
    ? tile.unit === "percentage"
      ? // Percentage tiles compare in points: 55% against 50% is +5, not +10%.
        // A subtraction, so a 0% baseline is valid ("0% had photos last
        // period") — unlike the count delta, nothing here divides by it. Only
        // an unknown comparison value suppresses the delta.
        value !== undefined && comparisonValue !== undefined
        ? Math.round(value - comparisonValue)
        : null
      : toPercentageDelta(value, comparisonValue)
    : null;

  const formatted =
    value === undefined
      ? "—"
      : tile.unit === "percentage"
        ? `${formatNumber(value)}%`
        : formatNumber(value);

  const renderedValue =
    delta !== null ? (
      <span className="inline-flex items-baseline gap-2">
        {formatted}
        <span
          className={cn(
            "inline-flex items-center gap-0.5 text-sm font-medium",
            delta >= 0 ? "text-success" : "text-destructive",
          )}
        >
          {delta >= 0 ? (
            <TrendingUp aria-hidden="true" className="size-3.5" />
          ) : (
            <TrendingDown aria-hidden="true" className="size-3.5" />
          )}
          <span className="sr-only">
            {delta >= 0 ? "gestegen met" : "gedaald met"}
          </span>
          {delta >= 0 ? "+" : ""}
          {delta}
          {tile.unit === "percentage" ? " pt" : "%"}
        </span>
      </span>
    ) : (
      formatted
    );

  return (
    <StatCard
      label={tile.label}
      icon={tile.icon}
      value={renderedValue}
      className="h-full"
    />
  );
}

/**
 * The KPI row's grid classes per configured tile count — count-aware at every
 * breakpoint, so a single tile stays single-column rather than rendering
 * half-width at `sm`. There are four KPI cases at most, so the full class
 * strings stay statically scannable; the default covers a future fifth.
 */
const KPI_GRID_CLASSES: Record<number, string> = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
};

/** The default: four tiles, and the fallback for any count above four. */
const KPI_GRID_CLASSES_DEFAULT = "sm:grid-cols-2 lg:grid-cols-4";

/**
 * The period-aware KPI row above the tabs, driven by the served
 * configuration's `kpis` list: the backend's resolver already dropped every
 * tile the current user may not read, so the row renders exactly what it is
 * handed, in configured order, and nothing at all when the list is empty (no
 * empty grid). The grid sizes itself to the configured tile count rather
 * than assuming four.
 */
function AnalyticsKpisSection({
  kpis,
  params,
  comparisonParams,
}: {
  readonly kpis: readonly ReportingKpi[];
  readonly params: AnalyticsParams;
  readonly comparisonParams: AnalyticsParams | undefined;
}): ReactNode {
  const motionInitial = useMotionInitial("hidden");

  if (kpis.length === 0) {
    return null;
  }

  return (
    <m.div
      role="group"
      aria-label="Kerncijfers"
      variants={staggerContainer}
      initial={motionInitial}
      animate="visible"
      className={cn(
        "grid gap-3",
        KPI_GRID_CLASSES[kpis.length] ?? KPI_GRID_CLASSES_DEFAULT,
      )}
    >
      {kpis.map((kpi) => {
        const tile = ANALYTICS_KPIS[kpi];
        return (
          <m.div key={kpi} variants={staggerItem}>
            <AnalyticsKpiCard
              tile={tile}
              params={params}
              comparisonParams={comparisonParams}
            />
          </m.div>
        );
      })}
    </m.div>
  );
}

/**
 * The report filter bar (RK-1230): the three filters the served layout
 * offers (`layout.data.filters`), each narrowing EVERY analytics query on
 * the page — the charts, the KPI row and the KPI comparison query all derive
 * their params from the same URL search state, so a KPI can never contradict
 * the charts beneath it. Mirrors the dossier overview bar exactly: a bare
 * three-state Combobox per boolean ("Alle" is the empty value and sends
 * nothing; both other options send a real boolean, including `false`, which
 * is a deliberate choice) and a bare MultiSelect for the brands, no visible
 * label — the placeholder names the filter and the selected value explains
 * itself, the same pattern the dossier overview settled on. A "Wissen" reset
 * shows while any filter is active.
 */
function ReportingFilterBar({
  filters,
  externalInsured,
  claimable,
  brand,
  endOfContract,
  dossierType,
  repairType,
  hasContractRules,
  onFiltersChange,
}: {
  /** The filters the served layout offers; anything else is not rendered. */
  readonly filters: readonly ReportingFilter[];
  readonly externalInsured: boolean | undefined;
  readonly claimable: boolean | undefined;
  readonly brand: readonly string[];
  readonly endOfContract: EndOfContract | undefined;
  readonly dossierType: string | undefined;
  readonly repairType: string | undefined;
  readonly hasContractRules: boolean;
  readonly onFiltersChange: (next: AnalyticsFilterSelection) => void;
}): ReactNode {
  const enabledFilters = useMemo(() => new Set(filters), [filters]);
  // The page mounts this bar only once the layout resolved and lists at
  // least one filter, so mounting is itself half the gate on the
  // brand-options request; the `enabled` flag is the other half — a layout
  // without the Merk filter never fetches the reference list.
  const vehicleBrandsQuery = useGetAdminVehicleBrands({
    query: { enabled: enabledFilters.has("brand") },
  });
  const brandOptions: ComboboxOption[] = useMemo(
    () => toVehicleBrandOptions(vehicleBrandsQuery.data?.member ?? []),
    [vehicleBrandsQuery.data],
  );

  // Three-state: `false` is an active filter too, so this cannot be a
  // truthiness check.
  const filtersActive =
    externalInsured !== undefined ||
    claimable !== undefined ||
    brand.length > 0 ||
    endOfContract !== undefined ||
    dossierType !== undefined ||
    repairType !== undefined;

  return (
    <fieldset className="rounded-2xl border border-border/70 bg-surface p-4 shadow-xs">
      <legend className="sr-only">Filter rapportage</legend>
      <div className="flex flex-wrap items-center gap-2">
        {enabledFilters.has("externalInsured") && (
          <Combobox
            aria-label="Filter op verzekering"
            placeholder="Verzekering"
            searchable={false}
            size="sm"
            value={booleanToOptionValue(externalInsured)}
            onValueChange={(next) =>
              onFiltersChange({
                externalInsured: optionValueToBoolean(next),
                claimable,
                brand,
                endOfContract,
                dossierType,
                repairType,
              })
            }
            options={EXTERNAL_INSURED_OPTIONS}
          />
        )}
        {enabledFilters.has("claimable") && (
          <Combobox
            aria-label="Filter op verhaalbaarheid"
            placeholder="Verhaalbaarheid"
            searchable={false}
            size="sm"
            value={booleanToOptionValue(claimable)}
            onValueChange={(next) =>
              onFiltersChange({
                externalInsured,
                claimable: optionValueToBoolean(next),
                brand,
                endOfContract,
                dossierType,
                repairType,
              })
            }
            options={CLAIMABLE_OPTIONS}
          />
        )}
        {enabledFilters.has("brand") &&
          (vehicleBrandsQuery.isError ? (
            <div
              role="alert"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <span>Merken konden niet worden geladen.</span>
              <button
                type="button"
                onClick={() => {
                  void vehicleBrandsQuery.refetch();
                }}
                className="font-medium text-primary transition-colors hover:text-primary/80"
              >
                Opnieuw proberen
              </button>
            </div>
          ) : (
            <MultiSelect
              aria-label="Filter op merk"
              placeholder="Alle merken"
              searchPlaceholder="Zoek een merk..."
              size="sm"
              values={brand}
              onValuesChange={(next) =>
                onFiltersChange({
                  externalInsured,
                  claimable,
                  brand: next,
                  endOfContract,
                  dossierType,
                  repairType,
                })
              }
              options={brandOptions}
              loading={vehicleBrandsQuery.isPending}
            />
          ))}
        {enabledFilters.has("matchedRule") && hasContractRules && (
          <Combobox
            aria-label={END_OF_CONTRACT_LABEL}
            placeholder={END_OF_CONTRACT_LABEL}
            searchable={false}
            clearable={false}
            size="sm"
            value={endOfContract ?? "all"}
            triggerLabel={`${END_OF_CONTRACT_LABEL}: ${endOfContract === "yes" ? "Ja" : endOfContract === "no" ? "Nee" : "Alle"}`}
            onValueChange={(next) =>
              onFiltersChange({
                externalInsured,
                claimable,
                brand,
                endOfContract: toEndOfContract(next),
                dossierType,
                repairType,
              })
            }
            options={END_OF_CONTRACT_OPTIONS}
          />
        )}
        {enabledFilters.has("dossierType") && (
          <Combobox
            aria-label="Filter op type"
            placeholder="Type"
            searchable={false}
            size="sm"
            value={dossierType ?? ""}
            onValueChange={(next) =>
              onFiltersChange({
                externalInsured,
                claimable,
                brand,
                endOfContract,
                dossierType: next || undefined,
                repairType,
              })
            }
            options={DOSSIER_TYPE_OPTIONS}
          />
        )}
        {enabledFilters.has("repairType") && (
          <Combobox
            aria-label="Filter op herstelmethode"
            placeholder="Herstelmethode"
            searchable={false}
            size="sm"
            value={repairType ?? ""}
            onValueChange={(next) =>
              onFiltersChange({
                externalInsured,
                claimable,
                brand,
                endOfContract,
                dossierType,
                repairType: next || undefined,
              })
            }
            options={REPAIR_TYPE_OPTIONS}
          />
        )}
        {filtersActive && (
          // A Combobox with a value renders its own icon-only clear button,
          // also named "Wissen": in the same bar, two controls with that name
          // are indistinguishable to a screen reader, so this one spells out
          // that it clears every filter. The visible text stays inside the

          // accessible name (WCAG 2.5.3).
          <button
            type="button"
            aria-label="Alle filters wissen"
            onClick={() =>
              onFiltersChange({
                externalInsured: undefined,
                claimable: undefined,
                brand: [],
                endOfContract: undefined,
                dossierType: undefined,
                repairType: undefined,
              })
            }
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <X aria-hidden="true" className="size-3.5" />
            Wissen
          </button>
        )}
      </div>
    </fieldset>
  );
}

/**
 * The "dossierKpis" top-level section: the inbox-style "Kerncijfers" tile
 * row (total/unread/mine/action-required), backed by the lightweight
 * `/admin/dossiers/stats` endpoint. Owns its query itself, mirroring every
 * chart card (`ReportingChartCard`) and `AnalyticsKpisSection`: only mounted
 * — and so only fetching — while the served configuration actually lists
 * this section, instead of firing unconditionally for an affiliate that
 * never configured it.
 */
function DossierKpisSection(): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  const statsQuery = useDossierStats();

  return (
    <m.div
      role="group"
      aria-label="Kerncijfers"
      variants={staggerContainer}
      initial={motionInitial}
      animate="visible"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      {statsQuery.isPending ? (
        <>
          <span role="status" aria-live="polite" className="sr-only">
            Kerncijfers worden geladen…
          </span>
          {REPORTING_KPIS.map((kpi) => (
            <Skeleton key={kpi} aria-hidden="true" className="h-24 w-full" />
          ))}
        </>
      ) : statsQuery.isError ? (
        <div
          role="alert"
          className="col-span-full flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-surface py-16 text-center shadow-xs"
        >
          <p className="text-muted-foreground">
            De kerncijfers konden niet worden geladen.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void statsQuery.refetch()}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : (
        REPORTING_KPIS.map((kpi) => {
          const tile = DOSSIER_KPI_TILE_META[kpi];
          return (
            <m.div key={kpi} variants={staggerItem}>
              <StatCard
                label={tile.label}
                icon={tile.icon}
                value={tile.value(statsQuery.data)}
                className="w-full"
              />
            </m.div>
          );
        })
      )}
    </m.div>
  );
}

/**
 * The "statusBreakdown" top-level section: the dashboard's "Werkverdeling"
 * status-distribution bar. Owns its two queries itself for the same reason
 * as {@link DossierKpisSection}: mounted, and so fetching, only while this
 * section is actually configured.
 */
function StatusBreakdownSection(): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  const statusStatsQuery = useDossierStatusStats();
  const statusLookup = useDossierStatusLookup();
  const statusSlices = deriveStatusSlices(
    statusStatsQuery.data?.member ?? [],
    statusLookup.lookup,
  );
  const isPending = statusStatsQuery.isPending || statusLookup.isPending;
  const isErrored = statusStatsQuery.isError || statusLookup.isError;

  if (isErrored) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-surface py-16 text-center shadow-xs"
      >
        <p className="text-muted-foreground">
          De werkverdeling kon niet worden geladen.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void statusStatsQuery.refetch();
            statusLookup.refetch();
          }}
        >
          Opnieuw proberen
        </Button>
      </div>
    );
  }

  if (!isPending && statusSlices.length === 0) {
    return null;
  }

  return (
    <m.div variants={staggerItem} initial={motionInitial} animate="visible">
      <Panel icon={ChartColumnBig} title="Werkverdeling">
        {isPending ? (
          <div
            role="status"
            aria-live="polite"
            className="flex h-3 items-center rounded-full bg-muted"
          >
            <span className="sr-only">Werkverdeling wordt geladen…</span>
          </div>
        ) : (
          <StatusDistributionBar slices={statusSlices} legendLayout="inline" />
        )}
      </Panel>
    </m.div>
  );
}

/**
 * The renderers for the top-level sections, keyed by section id so the
 * served configuration decides both which sections render and in what order
 * (`parseReportingLayout` deliberately preserves it). The KPI row is not a
 * section: it renders from `layout.data.kpis`, above the tabs. Module-scope
 * (not rebuilt per render): each entry is a component reference, not a
 * closure, so mounting `<SectionComponent />` is what starts its query —
 * see {@link DossierKpisSection}/{@link StatusBreakdownSection}.
 */
const SECTION_COMPONENTS: Record<TopLevelReportingSection, () => ReactNode> = {
  dossierKpis: DossierKpisSection,
  statusBreakdown: StatusBreakdownSection,
};

function ChartView({
  entry,
  data,
  labels,
}: {
  readonly entry: ReportingChartEntry;
  readonly data: unknown;
  /**
   * The resolved label map for breakdown charts: the entry's static `labels`,
   * or the backend enum labels once the entry's `useLabels` query resolved.
   */
  readonly labels?: Readonly<Record<string, string>>;
}): ReactNode {
  if (entry.chart.kind === "photosPerDossier") {
    return (
      <BreakdownBarList
        data={toPhotoCountSegments(data)}
        variant="bar"
        formatValue={(value) => `${value.toLocaleString("nl-NL")} dossiers`}
      />
    );
  }

  const entries = toBreakdownEntries(data);

  if (entry.chart.kind === "trend") {
    // The zero-fill range comes from the response's own resolved start/end,
    // not from the requested params — see toTrendRange's own comment for why
    // (an omitted `end` resolves against the backend's clock, not the
    // browser's).
    return (
      <TrendBarChart
        aria-label={entry.title}
        data={toTrendPoints(entries, toTrendRange(data))}
        formatLabel={(label) => formatDate(label) ?? label}
      />
    );
  }

  const segments = toChartSegments(entries, labels);

  return (
    <>
      {/* The card knows the segments, so it — not the primitive — reports a
          "Niet vastgelegd" bucket to the page's footnote registry. */}
      <NotRecordedReporter segments={segments} />
      {entry.chart.kind === "donut" ? (
        <BreakdownDonutChart aria-label={entry.title} data={segments} />
      ) : (
        <BreakdownBarList
          data={segments}
          variant={entry.chart.variant}
          formatValue={(value) => `${value.toLocaleString("nl-NL")} dossiers`}
        />
      )}
    </>
  );
}

/** Reports a muted ("Niet vastgelegd") segment to the footnote registry. */
function NotRecordedReporter({
  segments,
}: {
  readonly segments: readonly { readonly muted?: boolean }[];
}): ReactNode {
  useReportNotRecorded(segments.some((segment) => segment.muted === true));
  return null;
}

/**
 * Gates the entire page on {@link ANALYTICS_READ_PERMISSION} — the same
 * permission `shell/navItems.tsx` gates the "Rapportage" link on. Hiding the
 * link is not access control: a user who already knows the URL (or a
 * bookmark from before their permissions changed) can still navigate
 * straight to it. Mounting {@link ReportingPageContent} only once authorized
 * is what actually prevents that, not merely hiding its rendered output:
 * every hook the content calls — the layout query, every chart's query —
 * would otherwise still fire regardless of permission, since React runs a
 * component's hooks whenever it renders, wrapper or not.
 */
function ReportingPage(): ReactNode {
  const user = useCurrentUser();
  return hasPermission(user, ANALYTICS_READ_PERMISSION) ? (
    <ReportingPageContent />
  ) : (
    <ReportingForbidden />
  );
}

function ReportingPageContent(): ReactNode {
  const layout = useReportingLayout();
  const search = Route.useSearch();
  const routeNavigate = Route.useNavigate();
  // Resolve the picker's selection once per committed URL selection, not per
  // render: a relative preset re-resolving on every render would silently
  // shift its window at midnight, and two charts rendered either side of
  // that boundary would disagree. The memo key is the search params (a new
  // object only when the URL changes), so a reload or navigation re-resolves
  // while the range stays stable for as long as the selection stands.
  const selection = useMemo(() => toPeriodSelection(search), [search]);
  const chartRange = useMemo(
    () => selectionToChartRange(selection),
    [selection],
  );
  const comparisonChartRange = useMemo(() => {
    const comparisonStart = selection.comparisonRange
      ? parseISODate(selection.comparisonRange.start)
      : null;
    const comparisonEnd = selection.comparisonRange
      ? parseISODate(selection.comparisonRange.end)
      : null;
    return selection.compare && comparisonStart && comparisonEnd
      ? toAnalyticsRange(comparisonStart, comparisonEnd)
      : undefined;
  }, [selection]);

  // The report filters ride along on EVERY analytics query the
  // page issues: the charts, the KPI row and the KPI comparison query all
  // spread the same `filterParams` into their range, so a KPI can never
  // contradict the charts beneath it. `false` is sent, only `undefined`
  // omits the parameter (see `toAnalyticsFilterParams`).
  const filterParams = useMemo(
    () =>
      toAnalyticsFilterParams(
        {
          externalInsured: search.externalInsured,
          claimable: search.claimable,
          brand: search.brand,
          endOfContract: search.endOfContract,
          dossierType: search.dossierType,
          repairType: search.repairType,
        },
        layout.data?.filterOptions?.matchedRule?.ruleIds ?? [],
      ),
    [search, layout.data?.filterOptions],
  );
  const chartParams = useMemo(
    () => ({ ...chartRange, ...filterParams }),
    [chartRange, filterParams],
  );
  const comparisonChartParams = useMemo(
    () =>
      comparisonChartRange === undefined
        ? undefined
        : { ...comparisonChartRange, ...filterParams },
    [comparisonChartRange, filterParams],
  );

  // The filter bar commits through the URL, like the period picker: the
  // search params are the single state every query derives from, so a shared
  // or reloaded link reproduces the exact report. Cleared booleans become
  // `undefined`, which TanStack drops from the URL entirely.
  const setReportFilters = (next: AnalyticsFilterSelection): void => {
    void routeNavigate({
      search: (prev) => ({
        ...prev,
        externalInsured: next.externalInsured,
        claimable: next.claimable,
        brand: [...next.brand],
        endOfContract: next.endOfContract,
        dossierType: next.dossierType,
        repairType: next.repairType,
      }),
      replace: true,
    });
  };

  // Every configured section's data query fires in parallel with every
  // other configured section's — no serial waterfall between charts once
  // the layout resolves (see `ReportingChartCard`, `AnalyticsKpisSection`,
  // `DossierKpisSection`, `StatusBreakdownSection`). None of them fires
  // speculatively before the layout names them as configured, though: an
  // affiliate — or a user without the permission behind a section — must
  // never cause its request, so each section's query lives inside the
  // component that only mounts once `layout.data` actually lists it.
  const tabs = layout.data?.tabs ?? [];
  // `tab` comes from the URL, but the configured tab keys only exist once
  // the layout resolves. A deep link naming a tab this affiliate does not
  // have would leave the Tabs root on a value with no mounted trigger or
  // panel, blanking the strip — resolve the effective tab here so the URL
  // can never select something that is not rendered.
  const activeTab = tabs.some(
    (configuredTab) => configuredTab.key === search.tab,
  )
    ? search.tab
    : tabs[0]?.key;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">
            Rapportage
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Managementinformatie over dossiers, intake en herstel.
          </p>
        </div>
        <DateRangePicker
          aria-label="Rapportageperiode"
          value={selection}
          maxRangeDays={MAX_REPORT_PERIOD_DAYS}
          onValueChange={(next) => {
            void routeNavigate({
              search: (prev) => ({
                ...prev,
                ...dateRangeSelectionToSearch(next),
              }),
              replace: true,
            });
          }}
        />
      </div>
      <Separator />

      {layout.isPending ? (
        // Reserves the sections' footprint while the configuration resolves,
        // sized 1:1 to the default section count, so the page does not jump
        // once it arrives.
        <div role="status" aria-live="polite" className="flex flex-col gap-5">
          <span className="sr-only">Rapportage wordt geladen…</span>
          {DEFAULT_REPORTING_LAYOUT.sections.map((section) => (
            <Skeleton
              key={section}
              aria-hidden="true"
              className="h-24 w-full"
            />
          ))}
        </div>
      ) : layout.isError ? (
        // A genuine failure (not the 404 the hook resolves to the default
        // itself) must never render the default sections: the affiliate may
        // not have configured them.
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-surface py-16 text-center shadow-xs"
        >
          <p className="text-muted-foreground">
            De rapportage-indeling kon niet worden geladen.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void layout.refetch()}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : (
        <>
          {layout.data.sections.length === 0 &&
          layout.data.kpis.length === 0 &&
          tabs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-border/70 bg-surface py-16 text-center text-muted-foreground shadow-xs">
              <FolderOpen aria-hidden="true" className="size-6" />
              <p>Er zijn geen rapportage-onderdelen geconfigureerd.</p>
            </div>
          ) : (
            <>
              {/* The bar renders only the filters the served layout lists,
                  and only once there is something to filter (a configuration
                  serving only filters shows the empty state instead). */}
              {layout.data.filters.length > 0 && (
                <ReportingFilterBar
                  filters={layout.data.filters}
                  externalInsured={search.externalInsured}
                  claimable={search.claimable}
                  brand={search.brand}
                  endOfContract={search.endOfContract}
                  dossierType={search.dossierType}
                  repairType={search.repairType}
                  hasContractRules={
                    (layout.data.filterOptions?.matchedRule?.ruleIds.length ??
                      0) > 0
                  }
                  onFiltersChange={setReportFilters}
                />
              )}
              <AnalyticsKpisSection
                kpis={layout.data.kpis}
                params={chartParams}
                comparisonParams={comparisonChartParams}
              />
              {layout.data.sections.map((section) => {
                const SectionComponent = SECTION_COMPONENTS[section];
                return (
                  <Fragment key={section}>
                    <SectionComponent />
                  </Fragment>
                );
              })}
            </>
          )}

          {tabs.length > 0 && (
            // The provider renders the "Niet vastgelegd" footnote below the
            // tabs, and only while a mounted card reports such a bucket; a
            // tab switch unmounts the previous tab's cards (their
            // registration cleans up), and a period change re-reports on the
            // new data.
            <NotRecordedProvider>
              <Tabs
                variant="underline"
                value={activeTab}
                onValueChange={(value) => {
                  void routeNavigate({
                    search: (prev) => ({ ...prev, tab: value }),
                    replace: true,
                  });
                }}
                className="flex flex-col gap-5"
              >
                <TabsList>
                  {tabs.map((configuredTab) => (
                    <TabsTrigger
                      key={configuredTab.key}
                      value={configuredTab.key}
                    >
                      {configuredTab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {tabs.map((configuredTab) => {
                  const chartEntries = configuredTab.sections
                    .map((section) => ({
                      section,
                      entry: (
                        REPORTING_CHARTS as Partial<
                          Record<ReportingSection, ReportingChartEntry>
                        >
                      )[section],
                    }))
                    .filter(
                      (
                        item,
                      ): item is {
                        section: ReportingSection;
                        entry: ReportingChartEntry;
                      } => item.entry !== undefined,
                    );
                  // Trend cards are naturally wide (a day-by-day series reads
                  // poorly squeezed into half the tab), so they go full-width,
                  // above the rest. The rest sit in a CSS-columns flow rather
                  // than a grid: a grid row is as tall as its tallest cell, so
                  // a short card (e.g. a two-slice donut) next to a tall one
                  // (e.g. a ranked list of ~25 directions) leaves the gap
                  // beneath it empty; columns let the next card start right
                  // there instead. The trade-off is order: a grid reads
                  // left-to-right per row, columns read top-to-bottom per
                  // column, so the configured order becomes column order, not
                  // row order.
                  const trendEntries = chartEntries.filter(
                    (item) => item.entry.chart.kind === "trend",
                  );
                  const otherEntries = chartEntries.filter(
                    (item) => item.entry.chart.kind !== "trend",
                  );
                  return (
                    <TabsContent
                      key={configuredTab.key}
                      value={configuredTab.key}
                      className="flex flex-col gap-5"
                    >
                      {trendEntries.map(({ section, entry }) => (
                        <ReportingChartCard
                          key={section}
                          entry={entry}
                          params={chartParams}
                        />
                      ))}
                      {otherEntries.length > 0 && (
                        <div className="gap-5 md:columns-2">
                          {otherEntries.map(({ section, entry }) => (
                            <div
                              key={section}
                              className="mb-5 break-inside-avoid"
                            >
                              <ReportingChartCard
                                entry={entry}
                                params={chartParams}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </NotRecordedProvider>
          )}
        </>
      )}
    </div>
  );
}
